// Message the Host — /<slug>/message
// Any visitor can leave a message; stored in album owner's inbox.
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MessageSquare, ArrowLeft, Send } from "lucide-react";
import PageShell from "../components/PageShell";
import { Albums, Messages } from "../lib/api";

const NAME_KEY = "meyecam.display_name";

export default function MessageHostPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [album, setAlbum] = useState(null);
  const [name, setName] = useState("");
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    setName(localStorage.getItem(NAME_KEY) || "");
    Albums.get(slug).then(setAlbum).catch(() => navigate(`/${slug}`));
  }, [slug, navigate]);

  const onSend = async (e) => {
    e.preventDefault();
    if (!text.trim()) { toast.error("Write a message first"); return; }
    setSending(true);
    try {
      await Messages.send(slug, name.trim() || "Guest", text.trim());
      if (name.trim()) localStorage.setItem(NAME_KEY, name.trim());
      setSent(true);
      toast.success("Message sent!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send message");
    } finally {
      setSending(false);
    }
  };

  if (!album) {
    return <PageShell><div className="flex justify-center mt-24"><div className="meye-spinner" /></div></PageShell>;
  }

  return (
    <PageShell
      headerLeft={
        <button onClick={() => navigate(`/${slug}/gallery`)} className="meye-chip" style={{ padding: "8px 12px" }} data-testid="back-from-message">
          <ArrowLeft size={14} /> Back
        </button>
      }
    >
      <div className="flex flex-col items-center text-center mt-4" data-testid="message-host-page">
        <div
          className="flex items-center justify-center mb-4"
          style={{ width: 64, height: 64, borderRadius: 999, background: "rgba(127,75,255,0.14)", border: "1.5px solid #7F4BFF" }}
        >
          <MessageSquare size={26} color="#7F4BFF" />
        </div>
        <div className="text-2xl font-semibold tracking-tight">Message the host</div>
        <div className="mt-1 text-sm" style={{ color: "rgba(255,255,255,0.65)" }}>
          Say something to {album.host_name} about {album.name}.
        </div>
      </div>

      {sent ? (
        <div className="glass-card mt-6 p-5 text-center" data-testid="message-sent-confirm">
          <div className="text-lg font-semibold mb-1">Sent ✨</div>
          <div className="text-sm" style={{ color: "rgba(255,255,255,0.65)" }}>
            {album.host_name} will see your note in their inbox.
          </div>
          <div className="flex gap-2 mt-5">
            <button
              className="meye-chip flex-1 justify-center"
              onClick={() => { setSent(false); setText(""); }}
              data-testid="send-another-btn"
            >
              Send another
            </button>
            <button
              className="cta-pill flex-1"
              onClick={() => navigate(`/${slug}/gallery`)}
              data-testid="back-to-gallery-after-send"
            >
              Back to gallery
            </button>
          </div>
        </div>
      ) : (
        <form className="glass-card mt-6 p-5" onSubmit={onSend}>
          <div className="section-label mb-2">YOUR NAME</div>
          <input
            className="meye-input mb-4"
            placeholder="e.g. Rick"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={50}
            data-testid="message-name-input"
          />
          <div className="section-label mb-2">MESSAGE</div>
          <textarea
            className="meye-input"
            placeholder="Write something nice…"
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={5}
            maxLength={2000}
            style={{ resize: "vertical", minHeight: 120, fontFamily: "inherit" }}
            data-testid="message-text-input"
          />
          <div className="text-xs mt-1 text-right" style={{ color: "rgba(255,255,255,0.4)" }}>
            {text.length} / 2000
          </div>

          <button
            type="submit"
            className="cta-pill w-full mt-4"
            disabled={sending}
            data-testid="send-message-btn"
          >
            {sending ? <div className="meye-spinner" /> : <><Send size={16} /> Send message</>}
          </button>
        </form>
      )}
    </PageShell>
  );
}
